<script setup lang="ts">
/**
 * Formulaire de coordonnées du payeur (step Coordonnées).
 * Partagé entre /a/[slug] et /b/[slug].
 *
 * Variants :
 * - "a" : champ téléphone visible (adhésion)
 * - "b" : pas de téléphone affiché (billetterie)
 *
 * Focus : UNE bordure, noire — même règle partout (cf. utils/stylesChamps).
 */
import { useErreursEffaceesALaSaisie } from "~/composables/useErreursEffaceesALaSaisie";
import type { BuyerInfoModel } from "~/types/public-page";
import { exempleTelephone } from "@donoor/utils";
import { usePaysAssociation } from "~/composables/usePaysAssociation";
import { BORDURE_CHAMP_PUBLIC } from "~/utils/stylesChamps";

// Pays de l'association : un téléphone tapé sans indicatif s'y lit.
const paysAsso = usePaysAssociation();
const { t } = useI18n();

const props = defineProps<{
  modelValue: BuyerInfoModel;
  title: string;
  subtitle: string;
  variant: 'a' | 'b';
  icon?: string;
  /**
   * Erreurs de validation par champ (`firstName`, `lastName`, `email`,
   * `confirmEmail`, `phone`). Affichées SOUS le champ, bordure rouge.
   * Vide = aucune erreur.
   */
  erreurs?: Record<string, string>;
  /** Préfixe des `id` HTML — cible du défilement au premier champ fautif. */
  prefixeId?: string;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: BuyerInfoModel): void;
}>();

const buyer = computed({
  get: () => props.modelValue,
  set: (v) => emit('update:modelValue', v),
});

const emailMismatch = computed(() =>
  buyer.value.confirmEmail.length > 0 && buyer.value.email !== buyer.value.confirmEmail,
);

/** Id HTML stable d'un champ payeur. */
function idChamp(nom: string): string {
  return `${props.prefixeId ?? "payeur"}-${nom}`;
}

// Une erreur s'efface dès qu'on modifie le champ (cf. composable).
const { erreurDe } = useErreursEffaceesALaSaisie(
  () => props.erreurs,
  () => ({ ...props.modelValue }),
);

/** Bordure rouge quand le champ porte une erreur. */
function classesChamp(nom: string): string {
  const base = inputBaseClass.value;
  if (!erreurDe(nom)) return base;
  return `${base} border-red-500! dark:border-red-500!`;
}

/**
 * Téléphone : chiffres et séparateurs SEULEMENT.
 * `type="tel"` ne filtre rien — il change juste le clavier mobile.
 */
function filtrerTelephone(e: Event) {
  const cible = e.target as HTMLInputElement;
  const propre = cible.value.replace(/[^\d\s+().-]/g, "");
  if (cible.value !== propre) cible.value = propre;
  buyer.value = { ...buyer.value, phone: propre };
}

const inputBaseClass = computed(
  () =>
    `w-full px-4 py-3 rounded-xl bg-white dark:bg-[#0f0f0f] text-sm text-[#2d2e5c] dark:text-white placeholder-gray-400 transition-colors ${BORDURE_CHAMP_PUBLIC}`,
);

const confirmEmailClass = computed(() =>
  emailMismatch.value
    ? 'border-red-400 dark:border-red-600 bg-red-50 dark:bg-red-900/10 focus:border-red-500'
    : `bg-white dark:bg-[#0f0f0f] ${BORDURE_CHAMP_PUBLIC}`,
);

const confirmEmailRootClass = computed(
  () => 'w-full px-4 py-3 rounded-xl border text-sm text-[#2d2e5c] dark:text-white placeholder-gray-400 transition-colors focus:outline-none',
);
</script>

<template>
  <!-- Section À PLAT, sans cadre (Karim 2026-09-26) : ce formulaire vit
       toujours DANS la carte d'une étape. L'encadrer à son tour donnait une
       boîte dans la boîte — bordure, fond gris d'en-tête et second arrondi à
       quelques pixels du premier. Le titre et l'icône suffisent à ouvrir la
       section. -->
  <div>
    <div class="flex items-center gap-3 mb-4">
      <div
        class="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
        :class="variant === 'a' ? 'bg-[#212121] dark:bg-white' : ''"
        :style="variant === 'b' ? { background: 'var(--accent, #212121)' } : undefined"
      >
        <UIcon
          :name="icon || (variant === 'a' ? 'i-lucide-user' : 'i-lucide-credit-card')"
          class="size-3.5"
          :class="variant === 'a' ? 'text-white dark:text-[#212121]' : ''"
          :style="variant === 'b' ? { color: 'var(--accent-text, #fff)' } : undefined"
        />
      </div>
      <div>
        <p class="text-sm font-semibold text-[#2d2e5c] dark:text-white">{{ title }}</p>
        <p class="text-[11px] text-[#717198] dark:text-gray-500">{{ subtitle }}</p>
      </div>
    </div>

    <div class="space-y-4">
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label :for="idChamp('firstName')" class="block text-sm font-medium text-[#2d2e5c] dark:text-gray-300 mb-1.5">{{ t("commun.champs.prenom") }} <span class="text-red-500">*</span></label>
          <input :id="idChamp('firstName')" v-model="buyer.firstName" type="text" autocomplete="given-name" :placeholder="t('commun.champs.prenom')" :aria-invalid="!!erreurDe('firstName') || undefined" :class="classesChamp('firstName')" />
          <p v-if="erreurDe('firstName')" class="flex items-start gap-1.5 text-xs text-red-500 mt-1.5">
            <UIcon name="i-lucide-alert-circle" class="size-3.5 shrink-0 mt-px" /><span>{{ erreurDe('firstName') }}</span>
          </p>
        </div>
        <div>
          <label :for="idChamp('lastName')" class="block text-sm font-medium text-[#2d2e5c] dark:text-gray-300 mb-1.5">{{ t("commun.champs.nom") }} <span class="text-red-500">*</span></label>
          <input :id="idChamp('lastName')" v-model="buyer.lastName" type="text" autocomplete="family-name" :placeholder="t('commun.champs.nom')" :aria-invalid="!!erreurDe('lastName') || undefined" :class="classesChamp('lastName')" />
          <p v-if="erreurDe('lastName')" class="flex items-start gap-1.5 text-xs text-red-500 mt-1.5">
            <UIcon name="i-lucide-alert-circle" class="size-3.5 shrink-0 mt-px" /><span>{{ erreurDe('lastName') }}</span>
          </p>
        </div>
      </div>
      <div>
        <label :for="idChamp('email')" class="block text-sm font-medium text-[#2d2e5c] dark:text-gray-300 mb-1.5">{{ t("association.publicPage.buyerForm.emailLabel") }} <span class="text-red-500">*</span></label>
        <input :id="idChamp('email')" v-model="buyer.email" type="email" inputmode="email" autocomplete="email" placeholder="email@exemple.com" :aria-invalid="!!erreurDe('email') || undefined" :class="classesChamp('email')" />
        <p v-if="erreurDe('email')" class="flex items-start gap-1.5 text-xs text-red-500 mt-1.5">
          <UIcon name="i-lucide-alert-circle" class="size-3.5 shrink-0 mt-px" /><span>{{ erreurDe('email') }}</span>
        </p>
      </div>
      <div>
        <label :for="idChamp('confirmEmail')" class="block text-sm font-medium text-[#2d2e5c] dark:text-gray-300 mb-1.5">{{ t("association.publicPage.buyerForm.confirmationEmailLabel") }} <span class="text-red-500">*</span></label>
        <input
          :id="idChamp('confirmEmail')"
          v-model="buyer.confirmEmail"
          type="email"
          inputmode="email"
          :placeholder="t('association.publicPage.buyerForm.confirmezEmailPlaceholder')"
          :aria-invalid="emailMismatch || !!erreurDe('confirmEmail') || undefined"
          :class="[confirmEmailRootClass, confirmEmailClass]"
        />
        <p v-if="emailMismatch" class="text-xs text-red-500 mt-1">{{ t("association.publicPage.buyerForm.emailsNeCorrespondentPas") }}</p>
        <p v-else-if="erreurDe('confirmEmail')" class="flex items-start gap-1.5 text-xs text-red-500 mt-1.5">
          <UIcon name="i-lucide-alert-circle" class="size-3.5 shrink-0 mt-px" /><span>{{ erreurDe('confirmEmail') }}</span>
        </p>
      </div>
      <div v-if="variant === 'a'">
        <label :for="idChamp('phone')" class="block text-sm font-medium text-[#2d2e5c] dark:text-gray-300 mb-1.5">{{ t("commun.champs.telephone") }}</label>
        <input :id="idChamp('phone')" :value="buyer.phone" type="tel" inputmode="tel" autocomplete="tel" :placeholder="exempleTelephone(paysAsso)" :aria-invalid="!!erreurDe('phone') || undefined" :class="classesChamp('phone')" @input="filtrerTelephone" />
        <p v-if="erreurDe('phone')" class="flex items-start gap-1.5 text-xs text-red-500 mt-1.5">
          <UIcon name="i-lucide-alert-circle" class="size-3.5 shrink-0 mt-px" /><span>{{ erreurDe('phone') }}</span>
        </p>
      </div>
    </div>
  </div>
</template>
